import * as React from 'react';
import Box from '@mui/material/Box';
import { tokens } from '../DashboardTheme/DashboardTheme';
import { Kicker, BodyText } from '../Typography/Typography';
import Reveal from '../Reveal/Reveal';

/**
 * Section header: eyebrow, oversized condensed title with an outlined
 * ghost word behind it, and an optional lead paragraph.
 */
export default function SectionHeading({ kicker, title, ghost, lead, align = 'center', sx }) {
    const centered = align === 'center';

    return (
        <Reveal>
            <Box
                sx={{
                    position: 'relative',
                    px: { xs: 3, md: 6 },
                    pt: { xs: 9, md: 14 },
                    pb: { xs: 4, md: 6 },
                    textAlign: align,
                    ...sx,
                }}
            >
                {ghost && (
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            top: { xs: 0, md: -6 },
                            left: centered ? '50%' : 0,
                            transform: centered ? 'translateX(-50%)' : 'none',
                            fontFamily: tokens.display,
                            // Viewport-relative so the watermark always fits the
                            // screen — a fixed size overflows and shows only a
                            // fragment of the word on narrow phones.
                            fontSize: { xs: '15vw', sm: '17vw', md: 230 },
                            lineHeight: 1,
                            letterSpacing: '0.02em',
                            color: 'transparent',
                            WebkitTextStroke: '1.5px rgba(255, 255, 255, 0.15)',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            userSelect: 'none',
                        }}
                    >
                        {ghost}
                    </Box>
                )}

                <Box sx={{ position: 'relative' }}>
                    {kicker && (
                        <Kicker justifyContent={centered ? 'center' : 'flex-start'} sx={{ mb: 2 }}>
                            {kicker}
                        </Kicker>
                    )}

                    <Box
                        component="h2"
                        sx={{
                            m: 0,
                            fontFamily: tokens.display,
                            fontSize: { xs: 44, sm: 60, md: 82 },
                            lineHeight: 0.95,
                            letterSpacing: '0.01em',
                            color: tokens.white,
                        }}
                    >
                        {title}
                    </Box>

                    <Box
                        sx={{
                            width: 68,
                            height: 3,
                            mt: 2.5,
                            mx: centered ? 'auto' : 0,
                            background: `linear-gradient(90deg, ${tokens.red}, transparent)`,
                        }}
                    />

                    {lead && (
                        <BodyText textAlign={align} sx={{ mt: 3 }}>
                            {lead}
                        </BodyText>
                    )}
                </Box>
            </Box>
        </Reveal>
    );
}
